import { T } from 'gt-react';
import {
  CheckCircleIcon,
  CircleIcon,
  CircleDashedIcon,
} from '@phosphor-icons/react';
import type { TaskPhaseRollup, TaskChecklistItem } from '@erp/shared-utils';
import { cn } from '@/lib/utils';

/**
 * Derived-phase stepper for a typed task: one step per expected subtask type
 * key (declaration order), each showing whether a matching subtask is
 * missing, open, or closed — driven entirely by `rollupTaskPhase`'s pure
 * checklist rollup (nothing persisted).
 */
export function TaskPhaseStepper({
  rollup,
  typeNameByKey,
}: {
  rollup: TaskPhaseRollup;
  typeNameByKey: Map<string, string>;
}) {
  if (rollup.phase === 'no_checklist') return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-1 gap-y-2 rounded-md border border-border bg-white px-3 py-2"
      data-testid="task-phase-stepper"
    >
      {rollup.items.map((item, index) => (
        <StepperItem
          key={item.typeKey}
          item={item}
          label={typeNameByKey.get(item.typeKey) ?? item.typeKey}
          isLast={index === rollup.items.length - 1}
        />
      ))}
      <div className="ml-auto pl-3 text-[11px] text-muted-foreground">
        <PhaseLabel phase={rollup.phase} />{' '}
        <span className="tabular-nums">
          ({rollup.closed}/{rollup.total})
        </span>
      </div>
    </div>
  );
}

function StepperItem({
  item,
  label,
  isLast,
}: {
  item: TaskChecklistItem;
  label: string;
  isLast: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
          item.state === 'closed' &&
            'border-[oklch(0.82_0.1_150)] bg-[oklch(0.96_0.04_150)] text-[oklch(0.4_0.1_150)]',
          item.state === 'open' &&
            'border-[oklch(0.86_0.07_240)] bg-[oklch(0.96_0.03_240)] text-[oklch(0.42_0.13_250)]',
          item.state === 'missing' &&
            'border-stone-200 bg-stone-50 text-stone-500',
        )}
        data-state={item.state}
      >
        {item.state === 'closed' ? (
          <CheckCircleIcon className="size-3.5" weight="fill" />
        ) : item.state === 'open' ? (
          <CircleIcon className="size-3.5" weight="bold" />
        ) : (
          <CircleDashedIcon className="size-3.5" />
        )}
        {label}
      </span>
      {!isLast && <span className="h-px w-4 bg-border" aria-hidden />}
    </div>
  );
}

function PhaseLabel({ phase }: { phase: TaskPhaseRollup['phase'] }) {
  switch (phase) {
    case 'not_started':
      return <T context="derived task checklist phase">Not started</T>;
    case 'in_progress':
      return <T context="derived task checklist phase">In progress</T>;
    case 'complete':
      return <T context="derived task checklist phase">Complete</T>;
    default:
      return null;
  }
}
